import { useMySchoolDistrictsQuery } from "@crowdcoursing/api/graphql";
import { useSchoolDistrictCreateNewSchool } from "@crowdcoursing/api/openapi";
import { Grid, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";

import {
  CreateSchoolTemplate,
  type CreateSchoolTemplateProps,
  type District,
  type FormValues,
} from "../../templates/CreateSchoolTemplate/CreateSchoolTemplate";

// Component strings
const tNewSchoolDetails = "New School Details";
const tCreateFailed = "Failed to create school. Please try again.";
const tMissingDistrict = "Missing district information. Please select a valid district.";
const tSelectDistrict = "Please select a district.";

interface CreateSchoolProps {
  districtId: string;
  districtBubbleId: string;
  onClose: () => void;
}

// Default initial values
const initialValues: FormValues = {
  addressLine1: "",
  addressLine2: "",
  addressLine3: "",
  city: "",
  districtId: "",
  highestGrade: "",
  isCharterSchool: false,
  level: "",
  lowestGrade: "",
  phoneNumber: "",
  schoolName: "",
  schoolType: "",
  stateInitials: "",
  stateName: "",
  website: "",
  zipCode: "",
  zipCodePlus4: "",
};

export const CreateSchool = ({ districtId, districtBubbleId, onClose }: CreateSchoolProps) => {
  const [districts, setDistricts] = useState<District[]>([]);
  const [isLoadingDistricts, setIsLoadingDistricts] = useState(true);
  const [selectedDistrictBubbleId, setSelectedDistrictBubbleId] = useState<string>(districtBubbleId);

  // Initialize form values, optionally pre-selecting a district if provided
  const formInitialValues: FormValues = {
    ...initialValues,
    districtId: districtId || "",
  };

  const createSchoolMutation = useSchoolDistrictCreateNewSchool();
  const { data: districtsData, isLoading: isDistrictsLoading } = useMySchoolDistrictsQuery();

  // Load districts data
  useEffect(() => {
    if (!isDistrictsLoading && districtsData?.mySchoolDistricts) {
      const mappedDistricts = districtsData.mySchoolDistricts.map((district) => ({
        bubbleId: district.bubbleId,
        id: district.schoolDistrictId,
        name: district.name,
      }));
      setDistricts(mappedDistricts);
      setIsLoadingDistricts(false);
    }
  }, [isDistrictsLoading, districtsData]);

  // When districts change or district selection changes, update the selected bubbleId
  useEffect(() => {
    // If a district ID is provided and we have districts loaded, find its bubbleId
    if (districtId && districts.length > 0) {
      const selectedDistrict = districts.find((d) => d.id === districtId);
      if (selectedDistrict?.bubbleId) {
        setSelectedDistrictBubbleId(selectedDistrict.bubbleId);
      }
    }
  }, [districtId, districts]);

  const handleSubmit = useCallback<CreateSchoolTemplateProps["onSubmit"]>(
    async (values) => {
      try {
        // Find the bubbleId for the selected district if it wasn't provided
        let bubbleId = selectedDistrictBubbleId;

        // If no bubbleId is set but we have a districtId in the form, look it up
        if (!bubbleId && values.districtId) {
          const selectedDistrict = districts.find((d) => d.id === values.districtId);
          if (!selectedDistrict?.bubbleId) {
            return { FORM_ERROR: tMissingDistrict };
          }
          bubbleId = selectedDistrict.bubbleId;
        }

        if (!bubbleId) {
          return { FORM_ERROR: tSelectDistrict };
        }

        const requestBody = {
          addressLine1: values.addressLine1,
          addressLine2: values.addressLine2 || null,
          addressLine3: values.addressLine3 || null,
          city: values.city,
          districtId: values.districtId,
          highestGrade: values.highestGrade || null,
          isCharterSchool: values.isCharterSchool,
          level: values.level,
          lowestGrade: values.lowestGrade || null,
          phoneNumber: values.phoneNumber || null,
          schoolName: values.schoolName,
          schoolType: values.schoolType || null,
          stateInitials: values.stateInitials,
          stateName: values.stateName,
          website: values.website || null,
          zipCode: values.zipCode,
          zipCodePlus4: values.zipCodePlus4 || null,
        };

        await createSchoolMutation.mutateAsync({
          body: requestBody,
          pathParams: { bubbleId },
        });

        onClose();
      } catch {
        return { FORM_ERROR: tCreateFailed };
      }
    },
    [createSchoolMutation, selectedDistrictBubbleId, districts, onClose],
  );

  return (
    <Grid container>
      <Grid size={12}>
        <Grid container justifyContent="center">
          <Grid
            size={{
              md: 8,
              xs: 10,
            }}
          >
            <Typography sx={{ textAlign: "center" }} variant="h5">
              {tNewSchoolDetails}
            </Typography>
            <CreateSchoolTemplate
              districts={districts}
              initialValues={formInitialValues}
              isLoadingDistricts={isLoadingDistricts}
              onClose={onClose}
              onSubmit={handleSubmit}
            />
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default CreateSchool;
